/**
 * Company: SPHMedia
 * Description: [Sticky bar for articles with title, summary and share options also shows reading progress]
 */

import "./styles/stickyBar.css";

import MobilePlayBtn from "@assets/tts-icons/mobile-play-btn.svg";
import PauseBtn from "@assets/tts-icons/pause-btn.svg";
import PlayBtn from "@assets/tts-icons/play-btn.svg";
import AiSummary from "@blocks/AiSummary/AiSummary";
import ShareSocialButton from "@blocks/ShareSocial/ShareSocialButton";
import { ArticleGaEventsDataProps } from "@elements/ArticleParagraph/ArticleRegwall";
import { ReactElement } from "react";

import { useTtsWidget } from "./hooks/useTtsWidget";

type ArticleStickyBarProps = {
  articleId: string;
  title: string;
  progress: number;
  socialIconList: string[];
  pageUrl: string;
  englishHeadline?: string;
  englishSummary?: string;
  tmCustomGaEventsProps?: ArticleGaEventsDataProps;
};

/**
 * ArticleStickyBar
 * Description: [Sticky bar for articles with title, summary and share options also shows reading progress]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function ArticleStickyBar({
  articleId,
  title,
  progress,
  socialIconList,
  pageUrl,
  englishHeadline,
  englishSummary,
  tmCustomGaEventsProps,
}: ArticleStickyBarProps): ReactElement {
  const { ttsVisible, toggleTts, listenBtnRef, isPlayerReady } =
    useTtsWidget(articleId);

  return (
    <>
      <div className="v_stickyArticleNow">
        <div className="v_articleRow">
          <div className="v_articleColumn v_titleColumn">
            <div className="v_articleTitle">
              <h3 dangerouslySetInnerHTML={{ __html: title }} />
            </div>
          </div>

          <div className="v_articleColumn v_aiSummarySection">
            {englishHeadline && englishSummary && (
              <AiSummary
                englishHeadline={englishHeadline}
                englishSummary={englishSummary}
                customGaEvents={tmCustomGaEventsProps}
                buttonLabel="SUMMARISE"
              />
            )}
          </div>

          {isPlayerReady && (
            <div className="v_articleColumn v_listenSection">
              <button
                onClick={toggleTts}
                aria-pressed={ttsVisible}
                aria-label={ttsVisible ? "hide tts widget" : "show tts widget"}
                className="v_listenBtn"
                ref={listenBtnRef}
              >
                <img
                  src={ttsVisible ? PauseBtn : PlayBtn}
                  alt="play-icon"
                  aria-hidden="true"
                  width={20}
                  height={20}
                  className="hidden lg:inline"
                />
                <img
                  src={MobilePlayBtn}
                  alt="play-icon-mobile"
                  aria-hidden="true"
                  width={40}
                  height={40}
                  className="lg:hidden"
                />
                <span className="hidden lg:inline">கேளுங்கள்</span>
              </button>
            </div>
          )}

          <div className="v_articleColumn v_shareArticle">
            <ShareSocialButton
              socialIconList={socialIconList}
              pagePath={pageUrl}
              mbPopupClassName="absolute right-0"
            />
          </div>
        </div>

        <div className="v_pageMeter">
          <div className="v_pageProgress" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div
        className={`${ttsVisible ? "tts-widget-container-visible" : ""} tts-sticky-widget-container left-0 flex justify-center`}
      >
        <div
          id={`tts-sticky-widget-${articleId}`}
          className="w-full md:w-[500px] px-[10px]"
        ></div>
      </div>
    </>
  );
}
